package com.evoapps.evoshift.domain.model

/**
 * One person's month at one workplace, as the working-time record (ewidencja czasu pracy,
 * Kodeks pracy art. 149) lists it: every day, the hours worked, night hours, Sundays and
 * holidays, and absences.
 */
data class WorkingTimeSheet(
    val userId: String,
    val employeeName: String,
    val role: String?,
    val workplaceId: String,
    val workplaceName: String,
    val year: Int,
    val month: Int,
    /** Full-time working hours of the month in the workplace's market. */
    val normHours: Int,
    val days: List<WorkingTimeDay>,
    /** SH-258: the workplace's choices for the record (columns, hours, rounding); the times and hours above follow them. */
    val options: TimesheetReportSettings = TimesheetReportSettings()
) {
    val totalHours: Double get() = days.sumOf { it.hours }
    val nightHours: Double get() = days.sumOf { it.nightHours }
    val sundayAndHolidayHours: Double get() = days.filter { it.isSunday || it.isHoliday }.sumOf { it.hours }
    val absenceDays: Map<AbsenceType, Int> get() = days.mapNotNull { it.absence }.groupingBy { it }.eachCount()
}

data class WorkingTimeDay(
    val date: String,
    val isSunday: Boolean,
    val isHoliday: Boolean,
    val shifts: List<WorkingTimeEntry>,
    val absence: AbsenceType? = null
) {
    val hours: Double get() = shifts.sumOf { it.hours }
    val nightHours: Double get() = shifts.sumOf { it.nightHours }
}

/**
 * One shift: the clocked times when clocked, else the planned ones. SH-258: the times as the
 * record shows them (rounded where the workplace rounds), [hours] and [nightHours] counted from
 * them by the workplace's choice of hours.
 */
data class WorkingTimeEntry(
    val plannedStart: String,
    val plannedEnd: String,
    val actualStart: String?,
    val actualEnd: String?,
    val hours: Double,
    val nightHours: Double,
    /** SH-116: break minutes of a clocked shift (paid and unpaid; [hours] leaves the unpaid out). */
    val breakMinutes: Long = 0
) {
    val clocked: Boolean get() = actualStart != null && actualEnd != null
}
